import { RegisterOptions } from 'react-hook-form';

import { Currencies, CurrenciesOrdered, Currency } from '@column/column-ui-kit';

import countryCodeJSON from '~/data/wires/country-codes.json';
import type { Counterparty } from '~/repositories';
import type { FxQuote } from '~/typings/API';

/** One entry per country, by name: the JSON is ordered by currency code and lists Sierra Leone under both of its. */
export const COUNTRIES = countryCodeJSON
  .filter((entry, i, all) => all.findIndex((e) => e.countryCode === entry.countryCode) === i)
  .sort((a, b) => a.countryName.localeCompare(b.countryName));

export const countryName = (countryCode?: string) =>
  countryCodeJSON.find((entry) => entry.countryCode === countryCode)?.countryName;

export const currencyForCountry = (countryCode?: string) => {
  const currencyCode = countryCodeJSON.find((entry) => entry.countryCode === countryCode)?.currencyCode;
  return Currencies.find(({ code }) => code === currencyCode) ?? null;
};

const USD_CURRENCY = Currencies.find(({ code }) => code === 'USD') as Currency;

export const getCurrencyByCountryCode = (countryCode?: string): Currency =>
  currencyForCountry(countryCode) ?? USD_CURRENCY;

export const NON_USD_CURRENCY_OPTIONS = CurrenciesOrdered.filter(({ code }) => code !== 'USD').map(
  ({ code, name }) => ({
    label: `${name} (${code})`,
    value: code,
  })
);

const USD_25_MINIMUM_CURRENCIES = [
  'ALL',
  'AOA',
  'AMD',
  'AWG',
  'AZN',
  'BSD',
  'BBD',
  'BZD',
  'BAM',
  'BIF',
  'KMF',
  'CDF',
  'ERN',
  'FKP',
  'XPF',
  'GEL',
  'GIP',
  'GYD',
  'KRW',
  'LAK',
  'LRD',
  'LYD',
  'MGA',
  'MVR',
  'MRU',
  'MNT',
  'NAD',
  'ANG',
  'PYG',
  'MKD',
  'SHP',
  'RSD',
  'SLE',
  'SSP',
  'SRD',
  'TWD',
  'TJS',
  'YER',
  'BRL',
];

const USD_100_MINIMUM_CURRENCIES = [
  'BND',
  'BOB',
  'CLP',
  'CRC',
  'CVE',
  'DJF',
  'DOP',
  'ETB',
  'GMD',
  'GNF',
  'GTQ',
  'HTG',
  'JOD',
  'KGS',
  'KHR',
  'LSL',
  'MOP',
  'MWK',
  'MUR',
  'MZN',
  'NGN',
  'NIO',
  'NPR',
  'PEN',
  'PGK',
  'PKR',
  'RWF',
  'SBD',
  'SCR',
  'STN',
  'SZL',
  'TOP',
  'TZS',
  'VUV',
  'WST',
  'XAF',
  'XOF',
];

/** The smallest amount the API accepts in the currency, in USD cents. */
export const getMinimumAmountUsdCents = (currencyCode: string) => {
  if (USD_25_MINIMUM_CURRENCIES.includes(currencyCode)) {
    return 2500;
  }
  if (USD_100_MINIMUM_CURRENCIES.includes(currencyCode)) {
    return 10000;
  }
  return 100;
};

const getCurrencySymbol = (currencyCode: string) => {
  try {
    return (
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find(({ type }) => type === 'currency')?.value ?? currencyCode
    );
  } catch {
    return currencyCode;
  }
};

export const formatInternationalWireExchangeRate = (quote: FxQuote) =>
  `${getCurrencySymbol(quote.sellCurrencyCode)}1 ${quote.sellCurrencyCode} = ${getCurrencySymbol(
    quote.buyCurrencyCode
  )}${quote.rate} ${quote.buyCurrencyCode}`;

// Generated from the backend's per-country purpose code lists in pkg/swift/common (MY, PK, KW, QA, and VN for VND).
// The backend rejects codes outside these lists when it builds the SWIFT message; regenerate if those files change.
export const COUNTRY_PURPOSE_CODES: Record<string, Record<string, string>> = {
  MY: {
    '00000': 'Food, Animals',
    '01000': 'Beverages, tobacco',
    '02000': 'Crude Materials',
    '03000': 'Minerals, Fuel',
    '04000': 'Oils, Fats, waxes',
    '05000': 'Chemicals, related',
    '06000': 'Manufactured Goods',
    '07000': 'Machinery, transport',
    '07100': 'Power lines, cables',
    '08000': 'Misc. manufactured',
    '09000': 'Misc. transactions',
    '09700': 'Non-monetary gold',
    '10010': 'Goods for processing',
    '11110': 'Freight by air',
    '11120': 'Freight by sea',
    '11130': 'Freight by other',
    '11210': 'Passenger air fare',
    '11220': 'Passenger sea fare',
    '11230': 'Passenger other fare',
    '12110': 'Airport services',
    '12120': 'Port services',
    '12130': 'Other terminals',
    '12140': 'Postal, courier',
    '12210': 'Charter aircraft',
    '12220': 'Charter ships',
    '12230': 'Charter other',
    '12310': 'Lease aircraft',
    '12320': 'Lease ships',
    '12330': 'Lease transport',
    '12400': 'Salvage fees',
    '12500': 'Repair, Maintenance',
    '13110': 'Traveler purchases',
    '13210': 'Official travel',
    '13220': 'Short term worker',
    '13300': 'Pilgrimage',
    '13400': 'Medical-related',
    '13500': 'Education-related',
    '14110': 'Investment income',
    '14120': 'Portfolio income',
    '14130': 'Retained earnings',
    '14140': 'Non-res. insurance',
    '14150': 'Investment funds',
    '14210': 'Loan interest (rel.)',
    '14220': 'Loan interest (non-rel.)',
    '14230': 'Deposits interest',
    '14240': 'Bond interest',
    '14250': 'Market instrument',
    '14310': 'Wages in cash',
    '14320': 'Wages in kind',
    '14330': 'Social contributions',
    '14410': 'Taxes, products',
    '14420': 'Subsidies, products',
    '14430': 'Natural resource rent',
    '15100': 'Gov offices abroad',
    '15200': 'Intl organizations',
    '15300': 'Trade missions',
    '15400': 'Financing charges',
    '15500': 'BNM minting coins/notes',
    '16100': 'Communication services',
    '16210': 'Construction in MY',
    '16220': 'Construction abroad',
    '16311': 'High risk insurance',
    '16312': 'General insurance',
    '16313': 'Life insurance',
    '16314': 'Reinsurance payments',
    '16315': 'Insurance on goods',
    '16321': 'High risk claims',
    '16322': 'General claims',
    '16323': 'Life insurance claims',
    '16324': 'Reinsurance claims',
    '16325': 'Goods insurance claims',
    '16332': 'Auxiliary insurance',
    '16410': 'Charged financial svc',
    '16420': 'Margins on fin. instr.',
    '16430': 'Asset management',
    '16440': 'FISIM',
    '16510': 'Computer services',
    '16520': 'Information services',
    '16610': 'Royalties for IP',
    '16620': 'License fees for IP',
    '16710': 'Merchanting trade',
    '16720': 'Admin expense share',
    '16730': 'Research and development',
    '16740': 'Architectural, engineering',
    '16750': 'Agri/mining/processing',
    '16760': 'Advertising, research',
    '16771': 'Legal services',
    '16772': 'Accounting services',
    '16773': 'Management consulting',
    '16780': 'Lease dwellings/equip.',
    '16791': 'Trade-related services',
    '16792': 'Waste treatment',
    '16810': 'Audio-visual services',
    '16820': 'Health services',
    '16830': 'Education services',
    '16840': 'Heritage and recreation',
    '16850': 'Other personal services',
    '16910': 'Service refunds',
    '17010': 'Inter-company offsets',
    '17020': 'Company fund transfers',
    '17030': 'Bilateral trade',
    '17040': 'Same company fund xfer',
    '17060': 'Fund xfer diff. companies',
    '17070': 'Resident company xfer',
    '17080': 'Remittance settlements',
    '21110': 'Grants, aid, donations',
    '21120': 'Pension, gratuity',
    '21131': 'Gov income taxes',
    '21132': 'Gov fines, penalties',
    '21133': 'Gov social contributions',
    '21140': 'Compensation and pledges',
    '21201': 'Personal transfer',
    '21210': 'Grants and gifts',
    '21220': 'Worker remittances',
    '21241': 'Private income taxes',
    '21242': 'Private fines, penalties',
    '21243': 'Private social contrib.',
    '21244': 'Private social benefits',
    '21245': 'Nonlife insurance prems',
    '21246': 'Nonlife insur. claims',
    '21230': 'Legacies and prizes',
    '22130': 'Other gov transfers',
    '22220': 'Migrant transfers',
    '22230': 'Other private transfers',
    '23000': 'Non-financial assets',
    '31111': 'Long-term loan drawdown',
    '31112': 'Long-term loan repay',
    '31113': 'Long-term loan prepay',
    '31121': 'Short-term loan drawdown',
    '31122': 'Short-term loan repay',
    '31123': 'Short-term loan prepay',
    '31311': 'Non-partic. RPS issue',
    '31312': 'Non-partic. RPS redeem',
    '31411': 'Fin. lease extension',
    '31412': 'Fin. lease repayment',
    '31413': 'Fin. lease prepayment',
    '31511': 'Credit facility extension',
    '31911': 'Other long-term loan ext',
    '31912': 'Other long-term repay',
    '31913': 'Other long-term prepay',
    '31921': 'Other short-term loan ext',
    '31922': 'Other short-term repay',
    '31923': 'Other short-term prepay',
    '32100': 'Money market fund shares',
    '32200': 'Other investment shares',
    '33000': 'Employee stock options',
    '34000': 'Intl org subscriptions',
    '35130': 'Mergers and acquisitions',
    '35140': 'Equity inv. (non-MA)',
    '35200': 'Investment liquidation',
    '35300': 'Head office accounts',
    '36110': 'Corp stocks in MY',
    '36120': 'Corp stocks intl',
    '36130': 'Corp stocks by non-res.',
    '36140': 'Corp stocks non-res. MY',
    '36210': 'Bonds in MY',
    '36220': 'Bonds intl',
    '36230': 'Bonds by non-res.',
    '36240': 'Bonds non-res. MY',
    '36310': 'Money market MY',
    '36320': 'Money market intl',
    '36330': 'Money market non-res.',
    '36340': 'Money market non-res. intl',
    '36410': 'MY govt securities',
    '36420': 'Foreign govt securities',
    '37100': 'Derivatives - Swaps',
    '37200': 'Derivatives - Forwards',
    '37300': 'Derivatives - Futures',
    '37400': 'Derivatives - Options',
    '37900': 'Derivatives - Other',
    '39111': 'Real estate MY (comm.)',
    '39112': 'Real estate MY (resid.)',
    '39121': 'Real estate abroad (comm.)',
    '39122': 'Real estate abroad (resid.)',
    '39210': 'Deposits abroad',
    '39220': 'Deposits Labuan',
    '99999': 'Resident domestic xfer',
  },
  PK: {
    '9010': 'EARNINGS OF PAK SHIPPING CO',
    '9011': 'CHARTER OF PAK SHIPS W CREW',
    '9012': 'SUPPLY OF BUNKER OIL SHIPS',
    '9013': 'EARNINGS REPAIR MAINT SHIPS',
    '9014': 'REM BY FOREIGN SHIPPING CO',
    '9015': 'REFUND SEA TRANSPORTATION',
    '9016': 'EARNINGS OF PAK AIRLINES',
    '9017': 'CHARTER OF PAK AIRCRAFT W CREW',
    '9018': 'SUPPLY OF AVIATION FUEL',
    '9019': 'REPAIRS FOREIGN AIRCRAFT',
    '9020': 'REM BY FOREIGN AIRLINES',
    '9021': 'REFUND TRANSPORTATION AIR',
    '9022': 'REM RECRUITING AGENTS PASSAGE',
    '9023': 'EARNINGS OF PAK ROAD TRANSPORT',
    '9024': 'SURPLUS EARNINGS RAILWAYS',
    '9026': 'REFUND OTHER TRANSPORTATION',
    '9027': 'REM BY FREIGHT FWD SEA',
    '9028': 'REM BY FREIGHT FWD AIR',
    '9029': 'REM BY FREIGHT FWD LAND',
    '9025': 'EARNINGS LIQUID FUEL TRANSPORT',
    '9030': 'EARNINGS ELECTRICITY TRANSM',
    '9009': 'OTHER TRANSPORT SERVICES',
    '9091': 'POSTAL SERVICES',
    '9092': 'COURIER SERVICES',
    '9031': 'OFFICIAL TRAVEL GOV',
    '9032': 'OFFICIAL TRAVEL INT ORG',
    '9033': 'SURRENDER BAL OFF TRAVEL',
    '9041': 'BUSINESS TRAVEL NON RESIDENT',
    '9051': 'NON OFFICIAL NON COMM DEL',
    '9052': 'SURRENDER BAL NON OFFICIAL',
    '9061': 'HEALTH',
    '9071': 'STUDENTS',
    '9072': 'TRAINEES',
    '9073': 'STUDENTS TRAINEES ABROAD',
    '9081': 'TOURISTS PAK NATIONAL',
    '9082': 'TOURISTS FOREIGN NATIONAL',
    '9083': 'RELIGIOUS TRAVEL',
    '9084': 'SURRENDER BAL PAK NATIONAL',
    '9085': 'SURRENDER BAL HAJJ',
    '9086': 'SURRENDER BAL NON HAJJ',
    '9111': 'CONSTRUCTION SERVICES',
    '9121': 'LIFE INSURANCE PENSION SVC',
    '9124': 'REFUND OF INS PENSION SVC',
    '9141': 'NON LIFE INSURANCE SERVICES',
    '9146': 'REFUND OF INSURANCE',
    '9151': 'REINSURANCE LIFE',
    '9152': 'REINSURANCE NONLIFE',
    '9161': 'AUXILIARY TO INSURANCE SVC',
    '9171': 'BANK COMMISSION CHARGES',
    '9172': 'REM FOR GUARANTEES INVOLVED',
    '9173': 'NON BANK FINANCIAL SERVICES',
    '9174': 'REFUND OF FINANCIAL SERVICES',
    '9101': 'TELECOMMUNICATION SERVICES',
    '9102': 'CALL CENTERS',
    '9181': 'HARDWARE CONSULTANCY SERVICES',
    '9182': 'SOFTWARE CONSULTANCY SERVICES',
    '9183': 'MAINT REPAIRS COMPUTERS',
    '9184': 'EXPORT OF COMPUTER SOFTWARE',
    '9185': 'OTHER COMPUTER SERVICES',
    '9186': 'FREELANCE COMPUTER INFO SVC',
    '9191': 'EARNINGS JOURNALISTS AUTHORS',
    '9192': 'SUBSCRIPTION TO NEWSPAPERS',
    '9193': 'NEWS AGENTS CORRESPONDENTS',
    '9201': 'ROYALTIES TRADEMARKS',
    '9202': 'LICENSE FEE',
    '9211': 'MERCHANTING',
    '9212': 'TRADE RELATED SERVICES',
    '9221': 'CHARTER SHIPS WO CREW LEASE',
    '9222': 'CHARTER AIRCRAFT WO CREW',
    '9230': 'WASTE TREATMENT DEPOLLUTION',
    '9231': 'LEGAL SERVICES',
    '9232': 'ACCOUNTING, AUDITING TAX SVC',
    '9233': 'BUS MGMT CONSULTANCY PR',
    '9234': 'AGENCY COMMISSION',
    '9235': 'PRINTING CHARGES SECURITY DOCS',
    '9236': 'PROCESSING FEES ON GOODS',
    '9237': 'ADS, MKT RESEARCH POLLING',
    '9238': 'RESEARCH DEVELOPMENT',
    '9239': 'ARCH ENG TECHNICAL SVC',
    '9241': 'AGRI MINING ON SITE PROC',
    '9242': 'REFUND REBATE',
    '9243': 'RECEIPTS SECURITY DEPOSITS',
    '9244': 'MEDICAL SVC EXPORTS',
    '9247': 'MISC OTHER BUS SVC N I E',
    '9248': 'REVERSE MISC OTHER BUS SVC',
    '9249': 'OTHER FREE LANCE SERVICES',
    '9250': 'MAINTENANCE REPAIR SERVICES',
    '9251': 'AUDIOVISUAL RELATED SVC',
    '9261': 'EARNINGS PROFESSIONAL ARTISTS',
    '9262': 'OTHER PERSONAL, CULTURAL SVC',
    '9271': 'REM BY FOREIGN MISSIONS PAK',
    '9272': 'MILITARY UNITS AGENCIES',
    '9273': 'OTHER GOVERNMENT SERVICES',
    '9281': 'REM BY INT ORGANIZATIONS',
    '9291': 'COMPENSATION OF EMPLOYEES',
    '9301': 'PROFITS BRANCHES ABROAD',
    '9302': 'DIVIDENDS PK INV ABROAD',
    '9122': 'SURPLUS INSURANCE COS ABROAD',
    '9303': 'INCOME ON INVEST FUND SHARES',
    '9311': 'INTEREST ON DEBT ASSOC ENT',
    '9312': 'INTEREST ON OTHER FIN INSTR',
    '9322': 'DIVIDENDS GEN GOVT',
    '9323': 'DIVIDENDS BANKS',
    '9324': 'DIVIDENDS OTHER SECTORS',
    '9331': 'DIVIDENDS GEN GOVT',
    '9332': 'DIVIDENDS BANKS',
    '9333': 'DIVIDENDS OTHER SECTORS',
    '9341': 'BONDS NOTES GEN GOVT',
    '9351': 'BONDS NOTES BANKS',
    '9361': 'BONDS NOTES OTHER SECTORS',
    '9381': 'MONEY MARKET INSTR GEN GOVT',
    '9391': 'MONEY MARKET INSTR BANKS',
    '9401': 'MONEY MARKET INSTR OTH SEC',
    '9412': 'INTEREST ON DEPOSIT',
    '9414': 'DISCOUNT',
    '9421': 'INTEREST DEBT SERVICING REFUND',
    '9422': 'OTHERS',
    '9423': 'REFUND INTEREST ON BORROWING',
    '9426': 'RENT ON NATURAL RESOURCES',
    '9431': 'INTEREST ON DEPOSITS',
    '9432': 'INTEREST ON TRADE LOANS',
    '9433': 'DISCOUNT',
    '9434': 'OTHERS',
    '9435': 'REFUND S L BORROWINGS BANKS',
    '9441': 'INTEREST OTHER SECTORS',
    '9442': 'DISCOUNT TRADE BILLS OTH SEC',
    '9443': 'RENT OTHER SECTORS',
    '9444': 'OTHER INV INCOME N S E OTH',
    '9445': 'REFUND S L BORROWINGS OTH',
    '9448': 'REM OF PREMIUM FIN DERIV',
    '9451': 'TAXES AND DUTIES',
    '9452': 'CUSTOM DUTY IN GOLD',
    '9453': 'OFFICIAL DONATIONS BUDGET',
    '9454': 'OFFICIAL DONATIONS AID RELIEF',
    '9455': 'OFFICIAL DONATIONS MILITARY',
    '9456': 'OFFICIAL TRANSFERS REGULAR',
    '9457': 'OFFICIAL TRANSFERS TECHNICAL',
    '9458': 'ZAKAT REMITTANCES',
    '9459': 'SADAQAT REMITTANCES',
    '9461': 'OFFICIAL TRANSFERS N S E',
    '9462': 'REVERSE UNREQ TRANSFERS',
    '9463': 'SOCIAL CONTRIB TO GOVT',
    '9468': 'NET PREMIUMS NONLIFE INS',
    '9469': 'NONLIFE INS CLAIMS CALLS',
    '9470': 'SOCIAL CONTRIBUTIONS',
    '9471': "WORKERS' REMITTANCES",
    '9472': "WORKERS' REMITTANCES POSTAL",
    '9473': 'PRIVATE DONATIONS',
    '9474': 'IMPORT COST OF GOLD',
    '9475': 'KERB PURCHASES',
    '9476': 'PRIVATE TRANSFERS N S E',
    '9477': 'PENSION TRANSACTIONS',
    '9478': 'UTILITY BILLS OTHER FEE',
    '9479': 'HOUSING SCHEMES',
    '9481': 'INVESTMENT GRANTS CASH',
    '9482': 'GRANTS MILITARY ESTABLISHMENTS',
    '9483': 'TAXES DUTIES CAP TRANSFERS',
    '9484': 'OTHER CAPITAL TRANSFERS',
    '9491': 'INVESTMENT GRANTS CASH OTH',
    '9492': 'INVEST GRANTS LEGACIES GIFTS',
    '9493': "RECEIPTS MIGRANTS' LIABILITIES",
    '9494': 'OTHER CAPITAL TRANSFERS OTH',
    '9501': 'SALE LAND EMBASSY INSTITUTION',
    '9502': 'SALE INTANGIBLE ASSETS',
    '9503': 'SALE OF MARKETING ASSETS',
    '9521': 'RECEIPTS SHORT TERM CAPITAL',
    '9522': 'BORROWINGS BY BANKS ABROAD',
    '9523': 'BORROWINGS BY OTHERS ABROAD',
    '9524': 'BORROWINGS BANKS PAK FCA',
    '9525': 'WITHDRAWALS EQV PKR FCA',
    '9526': 'WITHDRAWAL EQV PKR DIR INV',
    '9527': 'WITHDRAWAL EQV PKR PORTFOLIO',
    '9528': 'WITHDRAWAL EQV PKR OTH PUR',
    '9529': 'WITHDRAWAL EQV PKR GEN GOV',
    '9530': 'WITHDRAWAL EQV PKR SP FCA',
    '9531': 'WITHDRAWAL PKR FCA NON RES',
    '9532': 'FCA OF RESIDENTS INDIVIDUALS',
    '9533': 'FCA OF RESIDENTS GEN GOVT',
    '9534': 'FCA OF RESIDENTS ENTERPRISES',
    '9535': 'FCA OF NON RESIDENTS',
    '9536': 'FCA NBFIs',
    '9537': 'SHORT TERM SWAPS INTERBANK',
    '9538': 'SHORT TERM SWAPS ABROAD',
    '9539': 'WITHDRAWAL PLACEMENTS BANKS',
    '9548': 'OPTIONS FORWARDS CONTRACTS PK',
    '9551': 'GEN GOVT EQUITY CAPITAL',
    '9552': 'OTHER SECTOR EQUITY CAPITAL',
    '9553': 'GEN GOVT OTHER CAPITAL',
    '9554': 'OTHER SECTOR OTHER CAPITAL',
    '9555': 'INVEST FUND SHARES GEN GOVT',
    '9556': 'INVEST FUND SHARES OTH SEC',
    '9561': 'GEN GOVT EQUITY CAPITAL',
    '9562': 'OTHER SECTOR EQUITY CAPITAL',
    '9563': 'GEN GOVT OTHER CAPITAL',
    '9564': 'OTHER SECTOR OTHER CAPITAL',
    '9565': 'OTHER SECTOR (LT) OTHER CAP',
    '9566': 'INVESTMENT FUND SHARES',
    '9572': 'EQUITY SECURITIES GEN GOVT',
    '9573': 'EQUITY SECURITIES BANKS',
    '9574': 'EQUITY SECURITIES OTH SEC',
    '9582': 'DEBT SEC BONDS GEN GOVT',
    '9583': 'DEBT SEC BONDS BANKS',
    '9584': 'DEBT SEC BONDS OTH SEC',
    '9592': 'MONEY MARKET INSTR GEN GOVT',
    '9593': 'MONEY MARKET INSTR BANKS',
    '9594': 'MONEY MARKET INSTR OTH SEC',
    '9595': 'INVESTMENT FUND SHARES GEN GOVT',
    '9596': 'INVESTMENT FUND SHARES BANKS',
    '9597': 'INVESTMENT FUND SHARES OTH SEC',
    '9602': 'OFF PORTFOLIO INV GEN GOVT',
    '9605': 'SALE OF NAT SAVINGS SCHEMES',
    '9606': 'OTHER PORTFOLIO INV GEN GOVT',
    '9607': 'BANKS SALE OF EQUITY SEC',
    '9608': 'OTHER SECTORS SALE OF EQUITY',
    '9609': 'OTH SEC (NOT SCRA) SALE EQ',
    '9612': 'GEN GOVT SALE OF DEBT SEC',
    '9613': 'BANKS SALE OF DEBT SEC',
    '9614': 'OTH SEC SALE OF DEBT SEC',
    '9622': 'GEN GOVT SALE OF MONEY MKT',
    '9623': 'BANKS SALE OF MONEY MKT',
    '9624': 'OTH SEC SALE OF MONEY MKT',
    '9625': 'INVESTMENT FUND SHARES',
    '9637': 'LONG TERM CAPITAL N S E',
    '9638': 'REPAYMENT OF LOANS FOREIGN',
    '9641': 'BORROWINGS BY BANKS ABROAD',
    '9642': 'BORROWINGS BANKS PAK FCA',
    '9643': 'LONG TERM SWAPS INTERBANK',
    '9644': 'LONG TERM SWAPS ABROAD',
    '9645': 'WITHDRAWAL PLACEMENTS PAK',
    '9646': 'WITHDRAWAL PLACEMENTS ABROAD',
    '9647': 'BORROWINGS BY OTHERS',
    '9648': 'FCA SPECIAL PERMISSION EQUITY',
    '9649': 'FCA SPECIAL PERMISSION DIRECT',
    '9650': 'SP FCA RESIDENT ENT DIRECT',
    '9651': 'FCA SPECIAL PERMISSION LOANS',
    '9652': 'QARZ E HASNA',
    '9653': 'INVESTMENT BY MIGRANTS',
    '9654': 'SP FCA RESIDENT ENT LT DIR',
    '9668': 'NET PREMIUMS LIFE INSURANCE',
    '9669': 'LIFE INSURANCE CLAIMS',
    '9673': 'PAK DIPLOMATIC MISSIONS REFUND',
    '9674': 'SHORT TERM DEBT SVC LOAN',
    '9675': 'REIMBURSEMENT',
    '9676': 'REV ENTRY DEBT SERVICING',
    '9677': 'LONG TERM DEBT SVC LOANS',
    '9678': 'REVERSAL OF OTHERS UNCLASS',
    '9681': 'TRANSACTIONS CURRENCY NOTES',
    '9682': 'BACK TO BACK CURRENCY TRANS',
    '9693': 'SURPLUS FUNDS FREIGHT FWD',
    '9694': 'REV SURPLUS FOR SHIPPING',
    '9695': 'REV SURPLUS FOR AIRLINES',
    '9697': 'REVERSAL SUPPLIERS’ CREDIT',
    '9698': 'FUNDS REC IN PAK OFFSHORE',
    '9699': 'FUNDS REC ONSHORE OFFSHORE',
    '9703': 'RECEIPTS COST OF EXPORT SAMPLES',
    '9707': 'REM FOR GOODS E COMM',
    '9708': 'REM FOR SVC E COMM',
  },
  KW: {
    BKIP: 'BankLoanAccruedInterest',
    BKPP: 'BankLoanPrincipalPaydown',
    BKFE: 'BankLoanFees',
    CDDP: 'CardDeferredPayment',
    CDOC: 'OriginalCredit',
    FCOL: 'FeeCollection',
    CBLK: 'CardBulkClearing',
    CDCB: 'CardPaymentWithCashBack',
    MTUP: 'MobileTopUp',
    ACCT: 'AccountManagement',
    DEPT: 'Deposit',
    CASH: 'CashManagementTransfer',
    PERS: 'PersontoPersonPayment',
    COLL: 'CollectionPayment',
    INTC: 'IntraCompanyPayment',
    INTP: 'Intra Party Payment',
    MGSC: 'FuturesInitialMarginSegregated',
    EXTD: 'ExchangeTradedDerivatives',
    FIXI: 'FixedIncome',
    SBSC: 'SecuritiesBuySellBuyBack',
    SCVE: 'PurchaseSaleOfServices',
    BEXP: 'BusinessExpenses',
    COMC: 'CommercialPayment',
    GDDS: 'PurchaseSaleOfGoods',
    GDSV: 'PurchaseSaleGoodsServices',
    SERV: 'ServiceCharges',
    SUBS: 'Subscription',
    SUPP: 'SupplierPayment',
    TRAD: 'Commercial',
    MP2B: 'MobileP2BPayment',
    ROYA: 'Royalties',
    CHAR: 'CharityPayment',
    COMT: 'ConsumerThirdPartyPayment',
    HLRP: 'HousingLoanRepayment',
    HLST: 'HomeLoanSettlement',
    INPC: 'InsurancePremiumCar',
    INTE: 'Interest',
    LIFI: 'LifeInsurance',
    PPTI: 'PropertyInsurance',
    INPR: 'InsurancePremiumRefund',
    INSC: 'PaymentOfInsuranceClaim',
    INSU: 'InsurancePremium',
    LOAN: 'Loan',
    LOAR: 'LoanRepayment',
    RINP: 'RecurringInstallment',
    PENO: 'EnforcementOrderPayment',
    RELG: 'RentalLeaseGeneral',
    TRFD: 'TrustFund',
    FORW: 'ForwardForeignExchange',
    ADVA: 'AdvancePayment',
    BCDM: 'BearerChequeDomestic',
    BCFG: 'BearerChequeForeign',
    CCRD: 'CreditCardPayment',
    DCRD: 'DebitCardPayment',
    EDUC: 'Education',
    CFEE: 'CancellationFee',
    CORT: 'TradeSettlementPayment',
    REBT: 'Rebate',
    FEES: 'PaymentOfFees',
    GIFT: 'Gift',
    IHRP: 'HirePurchaseAgreement',
    INSM: 'Installment',
    IVPT: 'InvoicePayment',
    REFU: 'Refund',
    FAML: 'Family Support',
    MSVC: 'MultipleServiceTypes',
    LEGE: 'Legal Expense',
    LEGC: 'Legal case',
    GOVT: 'GovernmentPayment',
    BRKF: 'BrokerageFee',
    ANNI: 'Annuity',
    CMDT: 'CommodityTransfer',
    DERI: 'Derivatives',
    PRME: 'PreciousMetal',
    DIVD: 'Dividend',
    FREX: 'ForeignExchange',
    INVS: 'InvestmentAndSecurities',
    SECU: 'Securities',
    TREA: 'TreasuryPayment',
    SAVG: 'Savings',
    IRES: 'InvestmentRealEstate',
    MDCS: 'MedicalServices',
    HLTI: 'HealthInsurance',
    ALLW: 'Allowance',
    BONU: 'BonusPayment',
    COMM: 'Commission',
    PENS: 'PensionPayment',
    SSBE: 'SocialSecurityBenefit',
    SALA: 'SalaryPayment',
    SPSP: 'SalaryPensionSumPayment',
    BENE: 'UnemploymentDisability',
    EOFS: 'EndOfServicePayment',
    TFLG: 'TradeFinanceLetterGuarantee',
    TFLC: 'TradeFinanceLetterCredit',
    TAXS: 'TaxPayment',
    INTX: 'IncomeTax',
    AIRB: 'Air',
    UBIL: 'Utilities',
    PHON: 'TelephoneBill',
    KEMB: 'KuwaitiEmbassiesTransfers',
    IEMB: 'InternationalEmbassiesTransfers',
    DELG: 'GovernmentDelegationTransfers',
    INMM: 'Money Market',
    INTQ: 'MM Islamic',
    ICBD: 'CBK Bonds Issuance',
    RCBD: 'CBK Bonds Redumption',
    ECTQ: 'TawarruqCBKBondsExecution',
    SCTQ: 'TawarruqCBKBondsSettlement',
    ITBD: 'Treasury Bonds Issuance',
    RTBD: 'TreasuryBondsRedemption',
    EPDT: 'PublicDebtTawarruqExecution',
    SPDT: 'PublicDebtTawarruqSettlement',
    BDKD: 'BulkDepositParticipantBank',
    BWKD: 'BulkWithdrawalParticipantBank',
    FCYB: 'ForexCurrencyBuyParticipantBank',
    FCYS: 'ForexCurrencySellParticipantBank',
    CDCD: 'NormalDepositParticipantBank',
    CWCD: 'NormalWithdrawalParticipantBank',
    CHTR: 'OnusInterAccountTransfer',
    ISUK: 'Sukuk Issuance',
    RSUK: 'Sukuk Redemption',
    ITBL: 'Treasury Bills Issuance',
    RTBL: 'TreasuryBillsRedemption',
    OTPT: 'Overtime Payment',
    LEPT: 'Leave encashment',
    SADJ: 'Salary Adjustment',
    DEDU: 'Deductions',
    FORM: 'Form',
    STMP: 'Stamp',
    DISC: 'Discounts',
    FISU: 'Financial support',
    INST: 'Installment',
    TRCO: 'Training course',
    CONT: 'Contract',
    DUES: 'Dues',
    TEND: 'Tender',
    TECO: 'Terminate contract',
    EXWO: 'Excellent work',
    BOAL: 'Book allowance',
    TICK: 'Travel tickets',
    CCST: 'Cash custody',
    RSIG: 'Resignation',
    SLON: 'Social loan',
    OFMI: 'Official mission',
    TEAM: 'Work teams',
    IBAL: 'Increase balance',
  },
  QA: {
    A1A01: 'Export bills',
    A1A02: 'Export collection',
    A1A03: 'Export advances',
    A2A01: 'Qatari ship surplus',
    A2A02: 'Foreign ship opex',
    A2A04: 'Export ship freight',
    A2A05: 'Crewed ship lease',
    A2A07: 'Qatari air surplus',
    A2A08: 'Foreign air opex',
    A2A10: 'Export air freight',
    A2A11: 'Crewed air lease',
    A2A13: 'Transport services',
    A2B01: 'Business travel',
    A2B08: 'Tourist FC surrender',
    A2C01: 'Life insurance',
    A2C02: 'Freight insurance',
    A2C03: 'General insurance',
    A2C04: 'Reinsurance',
    A2C05: 'Auxiliary insurance',
    A2C06: 'Insurance claims',
    A2D01: 'Construction in QA',
    A2E01: 'Hardware consulting',
    A2E02: 'Software install',
    A2E03: 'Data processing',
    A2E04: 'IT repair',
    A2E05: 'News services',
    A2E06: 'Information services',
    A2F01: 'Financial intermed.',
    A2F02: 'Investment banking',
    A2F03: 'Aux financial svcs',
    A2G01: 'Foreign embassies',
    A2G03: 'Intl institution',
    A2G07: 'Government services',
    A2H01: 'Audiovisual services',
    A2H02: 'Cultural services',
    A2J01: 'Merchanting',
    A2J02: 'Trade services',
    A2J03: 'Dry lease services',
    A2J04: 'Legal services',
    A2J05: 'Accounting services',
    A2J06: 'Management consulting',
    A2J07: 'Advertising research',
    A2J08: 'R&D services',
    A2J09: 'Technical services',
    A2J10: 'Agri mining services',
    A2J11: 'Office maintenance',
    A2J12: 'Distribution svcs',
    A2J13: 'Environmental svcs',
    A2J19: 'Other services',
    A2K01: 'Postal claims',
    A2K02: 'Courier claims',
    A2K03: 'Telecom claims',
    A2R01: 'Franchise IP svcs',
    A2R02: 'License originals',
    A3A03: 'Loan interest',
    A3A04: 'Debt security interest',
    A3A05: 'Own acct interest',
    A3A06: 'Profit repatriation',
    A3A07: 'Dividend receipts',
    A4A01: 'Family savings',
    A4B02: 'Gifts donations',
    A4B03: 'Religious charity',
    A4B04: 'Gov grants donations',
    A4B06: 'Tax refunds',
    A4B07: 'Employee compensation',
    A5A17: 'Intangible asset sale',
    A6A03: 'Branch inv repat',
    A6A04: 'Subsidiary inv repat',
    A6A05: 'Real estate inv repat',
    A6A06: 'FDI equity',
    A6A07: 'FDI debt securities',
    A6A08: 'FDI real estate',
    A6B01: 'Equity cap repat',
    A6B02: 'Debt security repat',
    A6B09: 'Portfolio equity',
    A6B10: 'Portfolio debt',
    A6C11: 'Loan repayment',
    A6C12: 'Non-resident loans',
    A6C13: 'Short-term loans',
    A6C14: 'Non-res deposits',
    A6C15: 'Bank own acct',
    A6C18: 'Other capital',
    A7A01: 'Import refunds',
    A7B02: 'Wrong entry reversal',
    A7C03: 'Resident receipts',
  },
  VN: {
    '1': 'EXPORT REVENUE',
    '2': 'ONE WAY PAYMENT',
    '3': 'FOREIGN DIRECT CAPITAL INJECTION',
    '4': 'SETTLEMENT FOR M AND A ACTIVITIES',
    '5': 'FOREIGN INDIRECT INVESTMENT EQUITY',
    '6': 'FOREIGN INDIRECT INVESTMENT BONDS',
    '8': 'ONSHORE FOREIGN CURRENCY BORROWING DISBURSEMENT',
    '9': 'VND FUNDS TRANSFER FROM FOREIGN REMTR TO VN BENE ACCT',
  },
};

// Mirrors the API's checks so the form flags the same fields it rejects: the create-time checks in
// pkg/transfers/swift/outgoing_destination.go and the code lists enforced in pkg/swift/mx/utils.go.

interface PurposeCodeFormat {
  pattern: RegExp;
  /** Completes "Purpose codes for this country are …". */
  description: string;
}

const PURPOSE_CODE_FORMATS: Record<string, PurposeCodeFormat> = {
  AE: { pattern: /^[\dA-Za-z]{3}$/, description: '3 letters or digits' },
  CN: { pattern: /^(?:CAP|GDS|SRV|CAC|FIF)$/, description: 'CAP, GDS, SRV, CAC, or FIF' },
  CO: { pattern: /^[A-Za-z\d]+$/, description: 'letters and digits only' },
  IN: { pattern: /^[PS]\d{4}$/, description: 'P or S followed by 4 digits' },
  KG: { pattern: /^\d{8}$/, description: '8 digits' },
  KW: { pattern: /^[A-Z]{4}$/, description: '4 capital letters' },
  KZ: { pattern: /^[\dA-Za-z]{10}$/, description: '10 letters or digits' },
  MY: { pattern: /^\d{5}$/, description: '5 digits' },
  PK: { pattern: /^\d{4}$/, description: '4 digits' },
  QA: { pattern: /^[A-Z]\d[A-Z]\d{2}$/, description: 'a letter, a digit, a letter, then 2 digits' },
  VN: { pattern: /^[1-689]$/, description: '1–6, 8, or 9' },
};

const CN_PURPOSE_CODES = ['CAP', 'GDS', 'SRV', 'CAC', 'FIF'];

// INR into India: the API refuses these codes outright, or asks for a more specific one.
const IN_INR_UNSUPPORTED_CODES = ['P1302', 'P1303', 'P1304'];
const IN_INR_TOO_GENERAL_CODES = ['P1499', 'P0099', 'P0001', 'P1011', 'P1099'];

const REMITTANCE_REQUIRED_COUNTRIES = ['MD', 'PK', 'VN'];

const PURPOSE_CODE_MAX_LENGTH = 35;
export const REMITTANCE_GENERAL_INFO_MAX_LENGTH = 140;
export const REMITTANCE_BENEFICIARY_REFERENCE_MAX_LENGTH = 30;

/** The API applies these rules by the country in the counterparty's BIC, so prefer that over the stored bank country. */
export const getBeneficiaryCountry = (counterparty?: Counterparty): string | undefined => {
  const routingNumber = counterparty?.routingNumber ?? '';
  if (counterparty?.routingNumberType === 'bic' && routingNumber.length >= 6) {
    return routingNumber.slice(4, 6).toUpperCase();
  }
  return counterparty?.localBankCountryCode?.toUpperCase() || undefined;
};

export const isPurposeCodeRequired = (country?: string, currency?: string): boolean =>
  !!country && country in PURPOSE_CODE_FORMATS && (currency !== 'USD' || country === 'QA');

export const getPurposeCodeFormat = (country?: string): PurposeCodeFormat | undefined =>
  country ? PURPOSE_CODE_FORMATS[country] : undefined;

/** The only codes the API accepts for the country, keyed to their descriptions (empty where it has none). */
export const getPurposeCodeList = (country?: string): Record<string, string> | undefined => {
  if (country === 'CN') {
    return Object.fromEntries(CN_PURPOSE_CODES.map((code) => [code, '']));
  }
  return country ? COUNTRY_PURPOSE_CODES[country] : undefined;
};

export const getPurposeCodeRules = (
  country?: string,
  currency?: string
): Pick<RegisterOptions, 'required' | 'maxLength' | 'validate'> => {
  const isRequired = isPurposeCodeRequired(country, currency);
  const format = getPurposeCodeFormat(country);
  const list = getPurposeCodeList(country);

  return {
    required: isRequired,
    maxLength: {
      value: PURPOSE_CODE_MAX_LENGTH,
      message: `Purpose code cannot exceed ${PURPOSE_CODE_MAX_LENGTH} characters.`,
    },
    validate: (value) => {
      const code = String(value ?? '').trim();
      if (!code) {
        return true;
      }
      if (list && !(code in list)) {
        return 'Choose a purpose code from the list for this country.';
      }
      if (format && isRequired && !format.pattern.test(code)) {
        return `Purpose codes for this country are ${format.description}.`;
      }
      if (country === 'IN' && currency === 'INR') {
        if (IN_INR_UNSUPPORTED_CODES.includes(code)) {
          return 'This purpose code is not supported for INR transfers to India.';
        }
        if (IN_INR_TOO_GENERAL_CODES.includes(code)) {
          return 'Please choose a more specific purpose code for INR transfers to India.';
        }
      }
      return true;
    },
  };
};

export const isRemittanceRequired = (country?: string): boolean =>
  !!country && REMITTANCE_REQUIRED_COUNTRIES.includes(country);
